import { inject, Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { isPresent } from 'services/operators';
import { createRequestOption, mapAsBody } from 'app/shared/util/request-util';
import { Offer } from '../models/offer.model';
import { IBaryTableFetchService } from './i-bary-table-fetch-service';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { ApiProviderService } from './api-provider.service';
import { OfferViewDTO } from '../models/offer-view-dto.type';

export type EntityResponseType = HttpResponse<OfferViewDTO>;
export type EntityArrayResponseType = HttpResponse<OfferViewDTO[]>;

@Injectable({ providedIn: 'root' })
export class OfferService implements IBaryTableFetchService<OfferViewDTO> {
  readonly #apiProvider = inject(ApiProviderService);

  protected resourceUrl = API_ENDPOINTS.OFFER;
  protected emptyUrl = API_ENDPOINTS.OFFER_EMPTY;
  protected offerUrl = API_ENDPOINTS.OFFER_TRANSPORTER_ID;
  protected insuranceItemsUrl = API_ENDPOINTS.OFFER_ID_INSURANCE_ITEMS;
  protected offerId = API_ENDPOINTS.OFFER_ID;
  protected offerDuplicateUrl = API_ENDPOINTS.OFFER_ID_DUPLICATE;

  fillDataToShow(entities: OfferViewDTO[]): Observable<OfferViewDTO[]> {
    return of(entities);
  }

  create(offer: OfferViewDTO): Observable<EntityResponseType> {
    return this.#apiProvider.post<OfferViewDTO>(this.emptyUrl, offer);
  }

  duplicateOffer(
    offerId?: number | undefined,
    name?: string,
    copyCutOff?: boolean | null,
    copyAcceptance?: boolean | null,
    copyPricing?: boolean | null
  ): Observable<OfferViewDTO> {
    return this.#apiProvider.post<OfferViewDTO>(`${this.offerDuplicateUrl(offerId)}`, { name, copyCutOff, copyAcceptance, copyPricing }).pipe(mapAsBody);
  }

  update(offer: OfferViewDTO): Observable<EntityResponseType> {
    return this.#apiProvider.put<OfferViewDTO>(`${this.resourceUrl}/${offer.id}`, offer);
  }

  find(id: number): Observable<EntityResponseType> {
    this.#apiProvider.get<OfferViewDTO[]>(this.resourceUrl, { observe: 'response' });
    return this.#apiProvider.publicGet<OfferViewDTO>(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.#apiProvider.get<OfferViewDTO[]>(this.resourceUrl, { params: options, observe: 'response' });
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.#apiProvider.delete(`${this.resourceUrl}/${id}`);
  }

  getTransporterOffersWithName(transporterId: number): Observable<EntityArrayResponseType> {
    return this.#apiProvider.get<OfferViewDTO[]>(this.offerUrl(transporterId), { observe: 'response' });
  }

  getTransportOffer(offerId: number): Observable<EntityResponseType> {
    return this.#apiProvider.publicGet<Offer>(this.offerId(offerId), { observe: 'response' });
  }

  setDefaultItemsForOffer(id: number): Observable<EntityArrayResponseType> {
    return this.#apiProvider.post<OfferViewDTO[]>(this.insuranceItemsUrl(id), null);
  }

  addOfferToCollectionIfMissing(offerCollection: OfferViewDTO[], ...offersToCheck: (OfferViewDTO | null | undefined)[]): OfferViewDTO[] {
    const offers: OfferViewDTO[] = offersToCheck.filter(isPresent);
    if (offers.length > 0) {
      const offerCollectionIdentifiers = offerCollection.map(offerItem => offerItem.id);
      const offersToAdd = offers.filter(offerItem => {
        const offerIdentifier = offerItem.id;
        if (offerIdentifier == null || offerCollectionIdentifiers.includes(offerIdentifier)) {
          return false;
        }
        offerCollectionIdentifiers.push(offerIdentifier);
        return true;
      });
      return [...offersToAdd, ...offerCollection];
    }
    return offerCollection;
  }
}
